import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { DateRangeValue } from '@/shared/components/ui/date-range-picker'
import { DATE_PRESETS, presetToRange } from '@/shared/constants/filter-options'
import { useAuthStore } from '@/shared/stores/auth.store'

/**
 * Shared filter state for /client-admin/overview and /client-admin/pipeline.
 *
 * Mirrors the queue-context.store snapshot pattern: sessionStorage-persisted
 * so a coder/admin who flips between Overview and Pipeline (or refreshes
 * either) keeps the same filter scope. Cleared when the tab closes — a fresh
 * browser session lands on defaults.
 *
 * Both pages own the same filter shape (clients / facilities / providers /
 * specialties / date range) and feed `EngineAdminFilters` + the date picker.
 * Holding the state in a single store means: pick a client on Overview →
 * land on Pipeline → same client preselected.
 */
const DEFAULT_PRESET = 'today'
function defaultDateRange(): DateRangeValue {
  const { date_from, date_to } = presetToRange(DEFAULT_PRESET)
  return { preset: DEFAULT_PRESET, date_from, date_to }
}

export interface ClientAdminDashboardFilters {
  clientIds: string[]
  facilityIds: string[]
  providerIds: string[]
  specialties: string[]
  dateRange: DateRangeValue
}

function defaultFilters(): ClientAdminDashboardFilters {
  return { clientIds: [], facilityIds: [], providerIds: [], specialties: [], dateRange: defaultDateRange() }
}

interface State extends ClientAdminDashboardFilters {
  /** User id the persisted filters currently belong to. Used to RESET (not
   *  restore per-identity) when a different identity reuses this tab's storage —
   *  these filters are ephemeral session state, so a reset is the safe behaviour. */
  ownerId: string | null
  setClientIds:    (v: string[]) => void
  setFacilityIds:  (v: string[]) => void
  setProviderIds:  (v: string[]) => void
  setSpecialties:  (v: string[]) => void
  setDateRange:    (v: DateRangeValue) => void
  /** Reset every filter back to defaults — mirrors the page-level "Clear filters" button. */
  reset: () => void
  /** Adopt the current identity; reset filters if a different user (or impersonated
   *  identity) reused this tab's storage. Idempotent for the same user. */
  claimOwner: (userId: string) => void
}

void DATE_PRESETS // ensure module side-effects (preset table) load before defaults

export const useClientAdminFiltersStore = create<State>()(
  persist(
    (set) => ({
      ownerId: null,
      ...defaultFilters(),
      setClientIds:    (v) => set({ clientIds: v }),
      setFacilityIds:  (v) => set({ facilityIds: v }),
      setProviderIds:  (v) => set({ providerIds: v }),
      setSpecialties:  (v) => set({ specialties: v }),
      setDateRange:    (v) => set({ dateRange: v }),
      reset: () => set(defaultFilters()),
      claimOwner: (userId) =>
        set((s) => (s.ownerId === userId ? {} : { ownerId: userId, ...defaultFilters() })),
    }),
    {
      name: 'client-admin-dashboard-filters',
      storage: createJSONStorage(() => sessionStorage),
      // Bump when the default date preset changes so existing sessions migrate
      // off the stale value instead of rehydrating it.
      version: 2,
      migrate: (persisted: any, _from) => {
        // Reset the dateRange to the new default; keep entity selections.
        if (persisted && typeof persisted === 'object') {
          persisted.dateRange = defaultDateRange()
        }
        return persisted
      },
      partialize: (s) => ({
        ownerId: s.ownerId,
        clientIds: s.clientIds,
        facilityIds: s.facilityIds,
        providerIds: s.providerIds,
        specialties: s.specialties,
        dateRange: s.dateRange,
      }),
    },
  ),
)

// Reset filters whenever the active user changes — a different login or an
// impersonated identity reusing this tab must not inherit the previous identity's
// saved client/facility selections. (These are ephemeral session filters, so we
// reset rather than keep a separate saved set per identity.) Runs outside React
// so every consumer page is covered without per-page wiring. Guarded on the full
// store API being present so this module-load side effect no-ops when useAuthStore
// is mocked as a bare hook in unit tests (getState/subscribe absent).
if (typeof useAuthStore.getState === 'function' && typeof useAuthStore.subscribe === 'function') {
  const uid0 = useAuthStore.getState().user?.id
  if (uid0) useClientAdminFiltersStore.getState().claimOwner(uid0)
  useAuthStore.subscribe((state, prev) => {
    const uid = state.user?.id
    if (uid && uid !== prev.user?.id) useClientAdminFiltersStore.getState().claimOwner(uid)
  })
}
